// Base API configuration
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://api-q5sqhbgfya-uc.a.run.app";

export class ApiError extends Error {
  code?: string;
  status?: number;

  constructor(message: string, options?: { code?: string; status?: number }) {
    super(message);
    this.name = "ApiError";
    this.code = options?.code;
    this.status = options?.status;
  }
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      mode: "cors", // Explicitly set CORS mode
      credentials: "omit", // Don't send credentials unless needed
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let message = `HTTP error! status: ${response.status}`;
        let code: string | undefined;
        try {
          const errorBody = await response.json();
          if (errorBody?.error) {
            message = errorBody.error;
          }
          if (typeof errorBody?.code === "string") {
            code = errorBody.code;
          }
        } catch {
          // ignore JSON parse errors
        }
        throw new ApiError(message, { code, status: response.status });
      }

      return response.json();
    } catch (error) {
      console.error("API request failed:", error);

      // Provide better error messages for CORS issues
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        console.warn(
          `CORS error or network failure for ${url}. This is expected in development if the API server is not configured for CORS or is not running.`,
        );
      }

      throw error;
    }
  }

  async get<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { method: "GET", ...options });
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit = {},
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit = {},
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE", ...options });
  }
  async patch<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit = {},
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }
}

export const apiClient = new ApiClient();
